import { getApiUrl } from '@/lib/api-config';
import { fetchEntity, fetchEntityList, fetchProjectLookup } from '@/lib/entity-api';
import { Invoice, InvoiceItem, Project, CreateInvoiceData, UpdateInvoiceData, CreateInvoiceItemData, UpdateInvoiceItemData } from '../types';

/**
 * Invoices data layer. Every read and write goes through the backend API: reads are the generic entity / lookups
 * reads (core/entity-read-routes.js), writes the generic entity API. The company and the author are never sent --
 * the server derives them from the session. The Invoices TABLE itself is the server-driven list
 * (hooks/useEntityList.ts); this is for what a screen needs besides it: one invoice with its items, the
 * invoices / projects of a dropdown, and the writes.
 */

async function send(path: string, method: 'POST' | 'PUT' | 'DELETE', body: unknown, failure: string) {
  const response = await fetch(getApiUrl(path), {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || failure);
  }
  return (await response.json().catch(() => ({}))).data;
}

export class InvoiceService {
  /** Every invoice of the company (or of one project) -- for a dropdown, NOT for a table. */
  static async getInvoices(projectId?: string): Promise<Invoice[]> {
    return (await fetchEntityList<any>('invoice', { projectId: projectId && projectId !== 'all' ? projectId : undefined })) as Invoice[];
  }

  /** One invoice with its items. */
  static async getInvoice(invoiceId: string): Promise<{ invoice: Invoice; items: InvoiceItem[] } | null> {
    const [invoice, items] = await Promise.all([
      fetchEntity<any>('invoice', invoiceId),
      fetchEntityList<any>('invoice_item', { filters: { invoice_id: invoiceId } }),
    ]);
    return { invoice, items: [...items].sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0)) };
  }

  static async createInvoice(invoiceData: CreateInvoiceData): Promise<Invoice> {
    // invoice_id and `number` are generated by the server; the company and the author come from the session
    const { company_id: _company, ...payload } = invoiceData;
    return send('api/entities/invoice', 'POST', {
      ...payload,
      project_id: payload.project_id || null,
      number: payload.number || null,
      name: payload.name || null,
      type: payload.type || null,
      due_date: payload.due_date || null,
      status: payload.status || 'open',
      total_amount: payload.total_amount || 0,
      notes: payload.notes || null,
      description: payload.description || null,
      tax_pct: payload.tax_pct || null,
      markup_pct: payload.markup_pct || null,
      other_amount: payload.other_amount || null,
    }, 'Failed to create invoice');
  }

  static async updateInvoice(invoiceData: UpdateInvoiceData): Promise<Invoice> {
    const { id, company_id: _company, ...changes } = invoiceData;
    return send(`api/entities/invoice/${id}`, 'PUT', changes, 'Failed to update invoice');
  }

  static async deleteInvoice(invoiceId: string): Promise<void> {
    await send(`api/entities/invoice/${invoiceId}`, 'DELETE', {}, 'Failed to delete invoice');
  }

  static async createInvoiceItem(itemData: CreateInvoiceItemData): Promise<InvoiceItem> {
    return send('api/entities/invoice_item', 'POST', itemData, 'Failed to create invoice item');
  }

  static async updateInvoiceItem(itemData: UpdateInvoiceItemData): Promise<InvoiceItem> {
    const { id, ...changes } = itemData;
    return send(`api/entities/invoice_item/${id}`, 'PUT', changes, 'Failed to update invoice item');
  }

  static async deleteInvoiceItem(itemId: string): Promise<void> {
    await send(`api/entities/invoice_item/${itemId}`, 'DELETE', {}, 'Failed to delete invoice item');
  }

  /** Every non-deleted project: an invoice may point at a finished one. */
  static async getProjects(): Promise<Project[]> {
    return (await fetchProjectLookup({ includeInactive: true })) as unknown as Project[];
  }
}
